/**
 * Alpine data factory for a custom cross-browser scrollbar.
 *
 * Slap `x-data="barrowsScrollbar"` on a `relative` wrapper whose first child holds
 * the scrollable content. The component will:
 *   1. Make the wrapper a `flex flex-col` so the scroll child can flex within it.
 *   2. Make the first child a `flex-1 min-h-0 overflow-auto` element (stays IN
 *      FLOW so its content contributes natural height up the flex chain — this is
 *      important for nested flex layouts where the wrapper has no explicit height).
 *   3. Inject a vertical and a horizontal custom track + thumb as absolute-positioned
 *      siblings overlaying the scroll element.
 *   4. Wire up scroll, resize, drag-to-scroll, and click-to-jump.
 *
 * Usage — minimum markup:
 *
 *   <div x-data="barrowsScrollbar" class="relative h-96">
 *     <div class="prose">… content …</div>
 *   </div>
 *
 * Works for vertical, horizontal, or both — whichever axis overflows shows its bar.
 */
export default () => ({
    // Declare state up front so each instance OWNS these keys. Assigning an
    // undeclared prop (this.scrollEl = …) writes to the outermost x-data scope
    // (the <body>), so every instance on the page shares one value — last wins.
    root: null,
    scrollEl: null,
    vTrack: null,
    vThumb: null,
    hTrack: null,
    hThumb: null,
    _ro: null,
    init() {
        this.root = this.$el;
        this.scrollEl = this.root.firstElementChild;
        if (!this.scrollEl) return;

        this.root.classList.add('flex', 'flex-col');

        this.scrollEl.classList.add(
            'flex-1',
            'min-h-0',
            'min-w-0',
            'overflow-auto',
            'barrows-scrollbar-hide-native'
        );

        // A11y: make the scrollable region keyboard-focusable. Once focused, the
        // browser natively handles Arrow keys, PgUp/PgDn, Home/End, and Space for
        // scrolling — no custom handlers needed. The custom track/thumb below are
        // decorative and marked aria-hidden so screen readers ignore them.
        // Consumers should add `role="region"` + `aria-label="…"` to the scroll
        // element themselves so the region gets a screen-reader-friendly name.
        if (!this.scrollEl.hasAttribute('tabindex')) {
            this.scrollEl.setAttribute('tabindex', '0');
        }

        this.vTrack = this._makeTrack('v');
        this.vThumb = this._makeThumb('v');
        this.vTrack.appendChild(this.vThumb);
        this.root.appendChild(this.vTrack);

        this.hTrack = this._makeTrack('h');
        this.hThumb = this._makeThumb('h');
        this.hTrack.appendChild(this.hThumb);
        this.root.appendChild(this.hTrack);

        this.update();
        this.scrollEl.addEventListener('scroll', () => this.update(), { passive: true });

        this._ro = new ResizeObserver(() => this.update());
        this._ro.observe(this.scrollEl);
        for (const child of this.scrollEl.children) {
            this._ro.observe(child);
        }
    },

    destroy() {
        this._ro && this._ro.disconnect();
        this.vTrack && this.vTrack.remove();
        this.hTrack && this.hTrack.remove();
    },

    _makeTrack(axis) {
        const el = document.createElement('div');
        el.className =
            axis === 'v'
                ? 'absolute top-0 right-0 bottom-0 w-1 rounded-full bg-barrows-gray-100 dark:bg-barrows-gray-450 cursor-pointer hidden'
                : 'absolute left-0 right-0 bottom-0 h-1 rounded-full bg-barrows-gray-100 dark:bg-barrows-gray-450 cursor-pointer hidden';
        // Decorative — visual scroll indicator only. Native scroll on the focused
        // scroll element handles all keyboard interaction, so the track + thumb
        // should be invisible to assistive tech.
        el.setAttribute('aria-hidden', 'true');
        el.addEventListener('click', (e) =>
            axis === 'v' ? this._trackClick(e, 'v') : this._trackClick(e, 'h')
        );
        return el;
    },

    _makeThumb(axis) {
        const el = document.createElement('div');
        el.className =
            axis === 'v'
                ? 'absolute -right-0.5 w-2 rounded-full bg-barrows-gray-400 dark:bg-white cursor-grab active:cursor-grabbing'
                : 'absolute -bottom-0.5 h-2 rounded-full bg-barrows-gray-400 dark:bg-white cursor-grab active:cursor-grabbing';
        // touch-action:none so a touch drag scrolls the thumb instead of the page
        el.style.touchAction = 'none';
        el.addEventListener('pointerdown', (e) =>
            axis === 'v' ? this._drag(e, 'v') : this._drag(e, 'h')
        );
        return el;
    },

    update() {
        const el = this.scrollEl;
        const TOL = 1;

        const showV = el.scrollHeight > el.clientHeight + TOL;
        this.vTrack.classList.toggle('hidden', !showV);
        if (showV) {
            this.vThumb.style.top = `${(el.scrollTop / el.scrollHeight) * 100}%`;
            this.vThumb.style.height = `${(el.clientHeight / el.scrollHeight) * 100}%`;
        }

        const showH = el.scrollWidth > el.clientWidth + TOL;
        this.hTrack.classList.toggle('hidden', !showH);
        if (showH) {
            this.hThumb.style.left = `${(el.scrollLeft / el.scrollWidth) * 100}%`;
            this.hThumb.style.width = `${(el.clientWidth / el.scrollWidth) * 100}%`;
        }
    },

    _drag(e, axis) {
        e.preventDefault();
        e.stopPropagation();

        const isV = axis === 'v';
        const thumb = isV ? this.vThumb : this.hThumb;

        // Capture the pointer to the thumb so every subsequent pointermove/up is
        // delivered here — even if the cursor leaves the thumb or the browser
        // would otherwise start a native drag/selection that swallows the move
        // events (which is exactly why the old window `mousemove` loop failed).
        thumb.setPointerCapture(e.pointerId);

        const start = isV ? e.clientY : e.clientX;
        const startScroll = isV ? this.scrollEl.scrollTop : this.scrollEl.scrollLeft;
        const scale = isV
            ? this.scrollEl.scrollHeight / this.scrollEl.clientHeight
            : this.scrollEl.scrollWidth / this.scrollEl.clientWidth;

        // The theme sets `scroll-behavior: smooth` globally (the `*` rule in the
        // base styles), so every programmatic scrollTop/scrollLeft write *animates*
        // toward its target. During a drag that means the scroll position eases
        // after the cursor instead of tracking it 1:1 — pronounced in Chrome,
        // slight in Safari. Force instant scrolling for the duration of the drag so
        // the thumb follows the cursor exactly, then restore on release.
        const prevBehavior = this.scrollEl.style.scrollBehavior;
        this.scrollEl.style.scrollBehavior = 'auto';

        const onMove = (ev) => {
            const delta = (isV ? ev.clientY : ev.clientX) - start;
            if (isV) this.scrollEl.scrollTop = startScroll + delta * scale;
            else this.scrollEl.scrollLeft = startScroll + delta * scale;
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            document.body.style.userSelect = '';
            this.scrollEl.style.scrollBehavior = prevBehavior;
        };
        document.body.style.userSelect = 'none';
        // Listen on window, not the thumb: pointer capture doesn't reliably keep
        // events on the 8px thumb once the cursor moves off it, but window always
        // sees them. Capture above still guards against native drag/selection.
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    },

    _trackClick(e, axis) {
        if (e.target !== e.currentTarget) return;
        const rect = e.currentTarget.getBoundingClientRect();
        if (axis === 'v') {
            const ratio = (e.clientY - rect.top) / rect.height;
            const max = this.scrollEl.scrollHeight - this.scrollEl.clientHeight;
            this.scrollEl.scrollTo({ top: ratio * max, behavior: 'smooth' });
        } else {
            const ratio = (e.clientX - rect.left) / rect.width;
            const max = this.scrollEl.scrollWidth - this.scrollEl.clientWidth;
            this.scrollEl.scrollTo({ left: ratio * max, behavior: 'smooth' });
        }
    },
});
